// src/utils/auth.js
const API_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';

async function readAuthResponse(response, fallbackMessage) {
  let data = {};

  try {
    data = await response.json();
  } catch (error) {
    data = {};
  }

  if (!response.ok || data.error) {
    throw new Error(data.error?.message || fallbackMessage);
  }

  return data;
}

export async function register({ // This function will now perform the multi-step registration
  firstName,
  lastName,
  organization,
  email,
  password,
  newsletterConsent, // Make sure this is passed from AuthForm
}) {
  let jwt;
  let userId;
  let finalUserData;

  try {
    // --- Call 1: Register Basic User with Strapi ---
    const registerResponse = await fetch(`${API_URL}/api/auth/local/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: email, // Using email as username by default
        email: email,
        password: password,
      }),
    });

    const registerData = await registerResponse.json();

    if (!registerResponse.ok || registerData.error) {
      // Handle errors from Strapi registration (e.g., email already taken)
      throw new Error(registerData.error?.message || 'Strapi registration failed');
    }

    jwt = registerData.jwt;
    userId = registerData.user.id;

    // --- Call 2: Update User Profile with Custom Fields ---
    // We use /api/users/:id because /api/users/me for PUT was problematic in your tests
    const updateUserResponse = await fetch(`${API_URL}/api/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        firstName: firstName,
        lastName: lastName,
        organization: organization,
        newsletterConsent: newsletterConsent,
        downloadedReports: [], // Initialize as empty array
      }),
    });

    finalUserData = await updateUserResponse.json(); // This will be the user object with custom fields

    if (!updateUserResponse.ok || finalUserData.error) {
      // Handle errors from updating user profile
      // Note: User is created in Strapi, but profile update failed.
      // You might want to inform the user or have a retry mechanism.
      // For now, we'll throw an error which stops the process.
      throw new Error(finalUserData.error?.message || 'Failed to update user profile');
    }

    // --- Call 3: Sync to Mailchimp (if consent given) ---
    // This assumes you will create the /api/custom-mailchimp-sync endpoint in Strapi
    if (newsletterConsent) {
      try {
        const mailchimpSyncResponse = await fetch(`${API_URL}/api/mcs-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // If you secure this endpoint in Strapi, you might need the JWT here too:
            // 'Authorization': `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            email: email,
            firstName: firstName,
            lastName: lastName,
            organization: organization,
            newsletterConsent: newsletterConsent, // Pass it for logging or conditional logic in the endpoint
          }),
        });

        const mailchimpSyncData = await mailchimpSyncResponse.json();

        if (!mailchimpSyncResponse.ok || mailchimpSyncData.error) {
          // Log Mailchimp sync error, but don't necessarily fail the whole registration for this.
          // The user is successfully registered in Strapi.
          console.warn('Mailchimp sync failed (user still registered in Strapi):', mailchimpSyncData.error?.message || mailchimpSyncData.message || 'Unknown Mailchimp sync error');
        } else {
          console.log('Mailchimp sync request sent successfully.');
        }
      } catch (mailchimpError) {
        console.warn('Mailchimp sync fetch error:', mailchimpError);
      }
    }

    // Return success indication, JWT for auto-login, and the updated user data
    return { success: true, jwt, user: finalUserData };

  } catch (error) {
    console.error('Full registration process error:', error);
    // Return an object that matches the error structure your AuthForm expects,
    // or a more generic error message.
    return { error: { message: error.message || 'An unknown error occurred during registration.' } };
  }
}

// Your other functions (login, setToken, getToken, logout) remain the same
export async function login({ identifier, password }) {
  const res = await fetch(`${API_URL}/api/auth/local`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, password }),
  });
  return res.json();
}

export async function requestPasswordReset(email) {
  const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  return readAuthResponse(res, 'Unable to send password reset email.');
}

export async function resetPassword({ code, password, passwordConfirmation }) {
  const res = await fetch(`${API_URL}/api/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, password, passwordConfirmation }),
  });

  return readAuthResponse(res, 'Unable to reset password.');
}

export function setToken(jwt) {
  localStorage.setItem('strapi_jwt', jwt);
}

export function getToken() {
  return localStorage.getItem('strapi_jwt');
}

export function logout() {
  localStorage.removeItem('strapi_jwt');
}
